import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { StatusVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, FileFilled, Upload } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsCandidatesDataCollectionDocument,
  AtsCandidatesDataCollectionItemFragment,
  AtsCandidatesDataCollectionJobPostingsFilterDocument,
  AtsCandidatesDataCollectionJobPostingsFilterFragment,
  AtsCandidatesDataCollectionSourcesFilterDocument,
  AtsCandidatesDataCollectionSourcesFilterFragment,
  AtsCandidatesDataCollectionTagsFilterDocument,
  AtsCandidatesDataCollectionTagsFilterFragment,
  AtsCandidatesDataCollectionToolbarActionsDocument,
  AtsCandidatesUpdateTalentPoolDocument,
  AtsCreatedApplicationsfromCandidatesFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { convertDate, dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { DeleteCandidateModal } from 'modules/ats/components/DeleteCandidateModal/DeleteCandidateModal'
import { UploadCvsModal } from 'modules/ats/components/UploadCvsModal'
import { AddCandidatesToJobPostingModal } from 'modules/ats/components/candidates/AddCandidateToJobPostingModal'
import { TalentPoolConfirmationModal } from 'modules/ats/components/candidates/TalentPoolConfirmationModal'
import { isConsentExpired } from 'modules/ats/lib/utils/candidate'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

const getAverageRatingStatus = (rating: number) => {
  if (rating <= 2) return 'critical'
  if (rating === 3) return 'warning'
  return 'positive'
}

const CANDIDATES_MODAL = {
  UPLOAD_CV: 'upload-cv',
  ADD_TO_JOB_POSTING: 'add-to-job-posting',
  DELETE_CANDIDATE: 'delete-candidate',
  TALENT_POOL: 'talent-pool',
} as const

type CandidatesModal = (typeof CANDIDATES_MODAL)[keyof typeof CANDIDATES_MODAL] | null

interface Props {
  showUploadCvsButton: boolean
}
export const CandidatesDataCollection = ({ showUploadCvsButton }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const { company } = useCurrentSession()

  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[] | null>(null)
  const clearSelectedCandidatesRef = useRef<(() => void) | null>(null)

  const [showModal, setModal] = useState<CandidatesModal>(null)
  const closeModal = () => setModal(null)

  const { data: toolbarData } = useQuery(AtsCandidatesDataCollectionToolbarActionsDocument, {
    variables: { companyId: company.id },
  })

  const [updateTalentPool] = useMutation(AtsCandidatesUpdateTalentPoolDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsCandidate,
  })

  const atsImportersFeatureEnabled =
    toolbarData?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'ats_importers'
    )?.enabled ?? false

  const atsBulkImportFeatureEnabled =
    toolbarData?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'ats_bulk_import'
    )?.enabled ?? false

  const canImportResources = toolbarData?.canImportResources._policies.importResources.can ?? false

  const canAddCandidate = toolbarData?.ats._policies.manageJobPostings.can ?? false
  const canUploadCvs = canAddCandidate && showUploadCvsButton
  const canImportCandidates = canAddCandidate && canImportResources && atsImportersFeatureEnabled
  const canBulkCandidatesImport = canAddCandidate && atsBulkImportFeatureEnabled

  const canBulkActions =
    toolbarData?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'ats_bulk_actions'
    )?.enabled ?? false

  const isConsentLimitEnabled =
    toolbarData?.ats.dataRetentionConfigurationsConnection.nodes[0]?.consentLimitEnabled ?? false

  const hasDuplicationServiceFeature =
    toolbarData?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'dev_ats_duplication_identification_service'
    )?.enabled ?? false

  const onClickAddToJobPosting = (id: number) => {
    setSelectedCandidateIds([id])
    setModal(CANDIDATES_MODAL.ADD_TO_JOB_POSTING)
    track(trackingEvents.ats.addCandidateToJobOpeningStart)
  }

  useEffect(() => {
    if (showModal === null) {
      setSelectedCandidateIds(null)
    }
  }, [showModal])

  const onCloseAddToJobPostingModal = () => {
    track(trackingEvents.ats.addCandidateToJobOpeningCancel)
    closeModal()
  }

  const onSuccessAddToJobPostingModal = (
    applications: AtsCreatedApplicationsfromCandidatesFragment[]
  ) => {
    clearSelectedCandidatesRef.current?.()
    if (applications.length > 0) {
      closeModal()
    } else {
      const application = applications[0]

      if (application?.id) {
        closeModal()
        track(trackingEvents.ats.addCandidateToJobOpeningSubmit, { is_successful: true })
        navigate(
          navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
            jobId: application.jobPosting.id,
            application_id: application.id,
          })
        )
      }
    }
  }
  const updateTalentPoolValue = async (id: number, talentPool: boolean) => {
    const { success } = await asyncAction(
      () => updateTalentPool({ variables: { id, talentPool: !talentPool } }),
      {
        successMessage: talentPool
          ? i18n.t('common.removed_successfully')
          : i18n.t('common.saved_successfully'),
      }
    )
    if (!talentPool) {
      track(trackingEvents.ats.addCandidateToTalentPool, {
        is_successful: success,
      })
    }
  }

  const onClickTalentPool = async ({
    id,
    consentToTalentPool,
    talentPool,
  }: {
    id: number
    consentToTalentPool: boolean
    talentPool: boolean
  }) => {
    const dontShowConfirmation =
      consentToTalentPool ||
      SafeLocalStorage.getItem(LOCAL_STORAGE_KEYS.atsTalentPoolConsentMessage, JSON.parse)

    if (talentPool || dontShowConfirmation) {
      await updateTalentPoolValue(id, talentPool)
    } else {
      setSelectedCandidateIds([id])
      setModal(CANDIDATES_MODAL.TALENT_POOL)
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: AtsCandidatesDataCollectionDocument,
      mapData: (data) => data.ats.candidatesConnection.nodes,
      mapPagination: (data) => ({
        total: data.ats.candidatesConnection.totalCount ?? 0,
      }),
      perPage: 20,
      mapVariables: ({ search, filters, sortings }) => {
        const jobPostingIds =
          filters?.jobPostingIds && filters?.jobPostingIds.length > 0
            ? { in: filters.jobPostingIds.map(Number).filter(Boolean) }
            : undefined

        const sourceIds =
          filters?.sources && filters?.sources.length > 0
            ? { in: filters.sources.map(Number).filter(Boolean) }
            : undefined

        const minimumAverageRating =
          filters?.rating && filters?.rating.length > 0
            ? { gteq: Math.min(...filters.rating.map(Number).filter(Boolean)) }
            : undefined

        const atsTagIds =
          filters?.tags && filters?.tags.length > 0
            ? { in: filters.tags.map(Number).filter(Boolean) }
            : undefined
        const consent =
          isConsentLimitEnabled && filters?.consent && filters?.consent.length > 0
            ? { in: filters.consent.filter(Boolean) }
            : undefined

        return {
          active: filters?.status?.includes('active') ?? false,
          archived: filters?.status?.includes('archived') ?? false,
          atsTagIds,
          consent,
          withDuplicates:
            hasDuplicationServiceFeature && filters?.alerts?.includes('duplicate')
              ? true
              : undefined,
          jobPostingIds,
          minimumAverageRating,
          search,
          sort: sortings?.[0],
          sourceIds,
          talentPool: filters?.status?.includes('talent_pool') ?? false,
        }
      },
      defaultSortings: {
        field: 'createdAt',
        order: 'desc',
      },
      search: {
        enabled: true,
      },
      filters: {
        status: {
          label: i18n.t('ats.candidates.list.filters.status_label'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('ats.candidates.list.filters.active_option'), value: 'active' },
              {
                label: i18n.t('ats.candidates.list.filters.talent_pool_option'),
                value: 'talent_pool',
              },
              { label: i18n.t('ats.candidates.list.filters.archived_option'), value: 'archived' },
            ],
          },
        },
        jobPostingIds: {
          label: i18n.t('ats.candidates.list.filters.job_postings_label'),
          type: 'in',
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: AtsCandidatesDataCollectionJobPostingsFilterDocument,
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.ats.jobPostingsConnection.nodes.map(
                  (jobPosting: AtsCandidatesDataCollectionJobPostingsFilterFragment) => ({
                    label: jobPosting.title || '',
                    value: jobPosting.id,
                  })
                ) || []
              )
            },
          },
        },
        sources: {
          label: i18n.t('ats.candidates.list.filters.sources_label'),
          type: 'in',
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: AtsCandidatesDataCollectionSourcesFilterDocument,
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.ats.candidateSources.map(
                  (candidateSource: AtsCandidatesDataCollectionSourcesFilterFragment) => ({
                    label: getSourceLabel(candidateSource.name) || '',
                    value: candidateSource.id,
                  })
                ) || []
              )
            },
          },
        },
        rating: {
          label: i18n.t('ats.candidates.list.filters.rating_label'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('ats.candidates.list.filters.rating_options.1'), value: 1 },
              { label: i18n.t('ats.candidates.list.filters.rating_options.2'), value: 2 },
              { label: i18n.t('ats.candidates.list.filters.rating_options.3'), value: 3 },
              { label: i18n.t('ats.candidates.list.filters.rating_options.4'), value: 4 },
              { label: i18n.t('ats.candidates.list.filters.rating_options.5'), value: 5 },
            ],
          },
        },
        ...(isConsentLimitEnabled && {
          consent: {
            label: i18n.t('ats.candidates.list.filters.consent_label'),
            type: 'in',
            options: {
              options: [
                {
                  label: i18n.t('ats.candidates.list.filters.consent_options.not_given'),
                  value: 'no_consent_given',
                },
                {
                  label: i18n.t('ats.candidates.list.filters.consent_options.given'),
                  value: 'not_expired',
                },
                {
                  label: i18n.t('ats.candidates.list.filters.consent_options.expired'),
                  value: 'expired',
                },
              ],
            },
          },
        }),
        tags: {
          label: i18n.t('ats.candidates.list.filters.tags_label'),
          type: 'in',
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: AtsCandidatesDataCollectionTagsFilterDocument,
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.ats.tags.map((tag: AtsCandidatesDataCollectionTagsFilterFragment) => ({
                  label: tag.name,
                  value: tag.id,
                })) || []
              )
            },
          },
        },
        ...(hasDuplicationServiceFeature && {
          alerts: {
            label: i18n.t('ats.candidates.list.filters.alerts_options.title'),
            type: 'in',
            options: {
              options: [
                {
                  label: i18n.t('ats.candidates.list.filters.alerts_options.duplicate'),
                  value: 'duplicate',
                },
              ],
            },
          },
        }),
      },
      presets: [
        {
          label: i18n.t('ats.candidates.list.filters.active_option'),
          filter: { status: ['active'] },
        },
        {
          label: i18n.t('ats.candidates.list.filters.talent_pool_option'),
          filter: { status: ['talent_pool'] },
        },
        {
          label: i18n.t('ats.candidates.list.filters.archived_option'),
          filter: { status: ['archived'] },
        },
        ...(hasDuplicationServiceFeature
          ? [
              {
                label: i18n.t('ats.candidates.list.filters.alerts_options.duplicate'),
                filter: { alerts: ['duplicate'] },
              },
            ]
          : []),
      ],
      itemUrl: (candidate: AtsCandidatesDataCollectionItemFragment) =>
        navigation.ats.candidates.id.applications.fullPath({ id: candidate.id }),
      primaryActions: () => {
        if (canAddCandidate) {
          return {
            label: i18n.t('ats.candidates.list.actions.new_candidate'),
            icon: Add,
            onClick: () => navigate(navigation.ats.candidates.new.fullPath()),
          }
        }
      },
      secondaryActions: () => {
        return [
          canUploadCvs && {
            label: i18n.t('ats.candidates.list.actions.upload_cvs'),
            icon: FileFilled,
            onClick: () => setModal(CANDIDATES_MODAL.UPLOAD_CV),
          },
          canAddCandidate && {
            label: i18n.t('ats.candidates.list.actions.add_internal_candidate'),
            icon: Add,
            onClick: () => navigate(navigation.ats.candidates.newEmployeeCandidate.fullPath()),
          },
          canBulkCandidatesImport && {
            label: i18n.t('ats.candidates.list.actions.import_candidates'),
            icon: Upload,
            onClick: () => navigate(navigation.ats.candidates.import.fullPath()),
          },
          canImportCandidates && {
            label: i18n.t('ats.candidates.list.actions.import_candidates_new'),
            icon: Upload,
            onClick: () =>
              navigate(navigation.ats.candidates.importCandidates.fullPath().replace(/\*+$/, '')),
          },
        ].filter(Boolean)
      },
      itemActions: (candidate: AtsCandidatesDataCollectionItemFragment) => [
        {
          label: i18n.t('ats.candidates.list.item_actions.add_to_job_opening'),
          onClick: () => onClickAddToJobPosting(candidate.id),
        },
        {
          label: candidate.talentPool
            ? i18n.t('ats.candidates.list.item_actions.remove_from_talent_pool')
            : i18n.t('ats.candidates.list.item_actions.add_to_talent_pool'),
          onClick: () =>
            onClickTalentPool({
              id: candidate.id,
              consentToTalentPool: candidate.consentToTalentPool ?? false,
              talentPool: candidate.talentPool ?? false,
            }),
        },
        {
          type: 'separator',
        },
        {
          label: i18n.t('common.delete'),
          critical: true,
          onClick: () => {
            setSelectedCandidateIds([candidate.id])
            setModal(CANDIDATES_MODAL.DELETE_CANDIDATE)
          },
        },
      ],
      selectable: canBulkActions ? (item) => item.id : undefined,
      bulkActions: () => {
        return {
          primary: [
            {
              label: i18n.t('ats.candidates.list.item_actions.add_to_job_opening'),
              icon: Add,
              id: 'add-to-job-posting-bulk',
              keepSelection: true,
            },
          ],
          secondary: [
            {
              label: i18n.t('common.delete'),
              icon: Delete,
              id: 'bulk-delete',
              keepSelection: true,
            },
          ],
        }
      },
    },
    [isConsentLimitEnabled, hasDuplicationServiceFeature]
  )

  const getConsentStatus = (
    consentExpirationDate: string | null
  ): { status: StatusVariant; label: string } => {
    if (!consentExpirationDate) {
      return {
        status: 'warning',
        label: i18n.t('ats.candidates.list.consent_expiration_date.pending'),
      }
    }

    if (isConsentExpired(consentExpirationDate)) {
      return {
        status: 'critical',
        label: i18n.t('ats.candidates.list.consent_expiration_date.expired_and_date', {
          date: dateBackendDateTimeToUI(consentExpirationDate),
        }),
      }
    }

    return {
      status: 'positive',
      label: i18n.t('ats.candidates.list.consent_expiration_date.given'),
    }
  }

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedItems, clearSelectedItems) => {
          if (action === 'add-to-job-posting-bulk') {
            const selectedIds = selectedItems.itemsStatus
              .filter((status) => status.checked)
              .map((status) => status.item.id)

            clearSelectedCandidatesRef.current = clearSelectedItems
            setSelectedCandidateIds(selectedIds)
            setModal(CANDIDATES_MODAL.ADD_TO_JOB_POSTING)
          }
          if (action === 'bulk-delete') {
            const selectedIds = selectedItems.itemsStatus
              .filter((status) => status.checked)
              .map((status) => status.item.id)

            setSelectedCandidateIds(selectedIds)
            setModal(CANDIDATES_MODAL.DELETE_CANDIDATE)
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  label: i18n.t('ats.candidates.list.title.full_name'),
                  render: ({
                    applications,
                    atsFiles,
                    firstName,
                    lastName,
                  }: AtsCandidatesDataCollectionItemFragment) => {
                    const applicationWithAvatar = applications.find((application) =>
                      application.files.some((file) => file.name === 'photo')
                    )
                    const avatar =
                      applicationWithAvatar?.files.find((file) => file.name === 'photo') ||
                      atsFiles.find((file) => file.category === 'photo')?.file

                    return {
                      type: 'person',
                      value: {
                        firstName,
                        lastName,
                        src: avatar?.url,
                      },
                    }
                  },
                  sorting: 'fullName',
                },
                {
                  label: i18n.t('ats.candidates.list.title.email'),
                  render: ({ email }: AtsCandidatesDataCollectionItemFragment) => email ?? '',
                },
                {
                  label: i18n.t('ats.candidates.list.title.phone'),
                  render: ({ phoneNumber }: AtsCandidatesDataCollectionItemFragment) =>
                    phoneNumber ?? '',
                },
                {
                  label: i18n.t('ats.candidates.list.title.jobs'),
                  render: ({ applications }: AtsCandidatesDataCollectionItemFragment) => {
                    if (applications.length > 0) {
                      return {
                        type: 'tagList',
                        value: {
                          type: 'dot',
                          max: 2,
                          tags: applications.map((application) => ({
                            text: application.relatedJobPosting.title,
                            color: phaseColor(
                              application.applicationPhase.hiringStage?.name || 'default'
                            ),
                          })),
                        },
                      }
                    }
                    return '-'
                  },
                },
                {
                  label: i18n.t('ats.candidates.list.title.source'),
                  render: ({
                    candidateSource,
                    medium,
                  }: AtsCandidatesDataCollectionItemFragment) => {
                    return {
                      type: 'tag',
                      value: {
                        label:
                          candidateSource && medium
                            ? `${medium} (${getSourceLabel(candidateSource.name)})`
                            : getSourceLabel(candidateSource?.name),
                      },
                    }
                  },
                },
                ...(hasDuplicationServiceFeature
                  ? [
                      {
                        label: i18n.t('ats.candidates.list.title.alerts'),
                        render: ({
                          duplicateIdentificationsAsMatched,
                          duplicateIdentificationsAsSource,
                        }: AtsCandidatesDataCollectionItemFragment) => {
                          return duplicateIdentificationsAsMatched.length > 0 ||
                            duplicateIdentificationsAsSource.length > 0
                            ? {
                                type: 'alertTag' as const,
                                value: {
                                  level: 'warning' as const,
                                  label: i18n.t(
                                    'ats.candidates.list.filters.alerts_options.duplicate'
                                  ),
                                },
                              }
                            : ''
                        },
                      },
                    ]
                  : []),
                {
                  label: i18n.t('ats.candidates.list.title.rating'),
                  render: ({ ratingAverage }: AtsCandidatesDataCollectionItemFragment) => {
                    if (ratingAverage) {
                      const rating = Math.floor(ratingAverage)

                      return {
                        type: 'status',
                        value: {
                          status: getAverageRatingStatus(rating),
                          label: rating,
                        },
                      }
                    }
                    return ''
                  },
                },
                {
                  label: i18n.t('ats.candidates.list.title.applied_on'),
                  render: ({ createdAt }: AtsCandidatesDataCollectionItemFragment) =>
                    convertDate(createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
                },
                ...(isConsentLimitEnabled
                  ? [
                      {
                        label: i18n.t('ats.candidates.list.title.consent'),
                        render: ({
                          consentExpirationDate,
                        }: AtsCandidatesDataCollectionItemFragment) => {
                          return {
                            type: 'status' as const,
                            value: getConsentStatus(consentExpirationDate),
                          }
                        },
                      },
                    ]
                  : []),
                {
                  label: i18n.t('ats.candidates.list.title.tags'),
                  render: ({ tags }: AtsCandidatesDataCollectionItemFragment) => {
                    return {
                      type: 'tagList',
                      value: {
                        type: 'raw',
                        max: 2,
                        tags: tags.map((tag) => ({
                          text: tag.name,
                        })),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />

      {showModal === CANDIDATES_MODAL.UPLOAD_CV && <UploadCvsModal onClose={closeModal} />}

      {showModal === CANDIDATES_MODAL.ADD_TO_JOB_POSTING && selectedCandidateIds && (
        <AddCandidatesToJobPostingModal
          candidateIds={selectedCandidateIds}
          onSuccess={(applications) => onSuccessAddToJobPostingModal(applications)}
          onClose={onCloseAddToJobPostingModal}
        />
      )}

      {showModal === CANDIDATES_MODAL.DELETE_CANDIDATE && selectedCandidateIds && (
        <DeleteCandidateModal candidateIds={selectedCandidateIds} onClose={closeModal} />
      )}

      {showModal === CANDIDATES_MODAL.TALENT_POOL &&
        selectedCandidateIds?.length === 1 &&
        selectedCandidateIds[0] !== undefined && (
          <TalentPoolConfirmationModal candidateId={selectedCandidateIds[0]} onClose={closeModal} />
        )}
    </>
  )
}
